import React, { ComponentProps, useMemo } from "react";
import { Image, Stack } from "react-bootstrap";
import {
  FiatCurrency,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./TopupBundle.scss";
import ImageHyperCoinStack from "../../../images/hyper-coin-stack.svg";
import ImageBonusHyperCoinStack from "../../../images/bonus-hyper-coin-stack.svg";
import ImageTwoGems from "../../../images/two-gems.svg";
import {
  calculateOriginalBundlePrice,
  formatCurrency,
  formatHprBalance,
} from "../../../utils/helpers";
import {
  BONUS_GEM_MARKER,
  BONUS_HPR_MARKER,
  SPECIAL_OFFER_MARKER,
} from "../../../utils/constants";
import Ribbon from "../../icons/Ribbon";
import { ExtraTag } from "../ExtraTag/ExtraTag";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import CheckmarkCircleIcon from "../../icons/CheckmarkCircleIcon";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

type TopupBundleProps = {
  plan: PurchasePlan;
  metadataObj?: Record<string, number>;
  isSelected?: boolean;
  isSpecialOffer?: boolean;
  isBestDeal?: boolean;
  isStandalone?: boolean;
  theme?: ThemeVariant;
  forceFiatDisplay?: boolean;
  fiatDisplayCurrency?: FiatCurrency;
};

function BonusTag() {
  return (
    <div className="BonusTag">
      <Ribbon className="Ribbon" />
      <span className="Grotesk SpecialFontSettings BonusText">Bonus</span>
    </div>
  );
}

function TopupBundle({
  plan,
  metadataObj,
  isSelected = false,
  isSpecialOffer,
  isBestDeal,
  isStandalone = false,
  theme = ThemeVariant.LIGHT,
  forceFiatDisplay = false,
  fiatDisplayCurrency,
  ...restProps
}: TopupBundleProps & ComponentProps<"button">) {
  const { t } = useTranslation("wallet");
  const { amount, price, sourceCurrency } = plan;
  const bonusHprOffer = metadataObj ? metadataObj[BONUS_HPR_MARKER] : 0;
  const bonusGemOffer = metadataObj ? metadataObj[BONUS_GEM_MARKER] : 0;
  const specialOffer = metadataObj ? metadataObj[SPECIAL_OFFER_MARKER] : 0;

  const finalBonusHprOffer = useMemo(
    () => (isSpecialOffer ? specialOffer - amount : bonusHprOffer),
    [amount, bonusHprOffer, isSpecialOffer, specialOffer],
  );

  const finalBonusGemOffer = useMemo(
    () => (isSpecialOffer ? 0 : bonusGemOffer),
    [bonusGemOffer, isSpecialOffer],
  );

  const hasBonusOffer = useMemo(() => {
    if (isSpecialOffer) return finalBonusHprOffer > 0;
    return finalBonusHprOffer > 0 || bonusGemOffer > 0;
  }, [isSpecialOffer, bonusGemOffer, finalBonusHprOffer]);

  const originalBundlePrice = useMemo(
    () =>
      calculateOriginalBundlePrice(
        price,
        amount,
        isSpecialOffer ? specialOffer : undefined,
        !isSpecialOffer ? bonusHprOffer : undefined,
      ),
    [amount, bonusHprOffer, isSpecialOffer, price, specialOffer],
  );

  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter({
    forceFiatDisplay,
    currency: fiatDisplayCurrency,
  });
  const hprDisplayValue = formatHprToFiatValue(amount);
  const bonusHprDisplayValue = formatHprToFiatValue(finalBonusHprOffer);

  return (
    <button
      className={`TopupBundle TopupBundle--${theme.toLowerCase()}`}
      {...restProps}
    >
      <div className={`Grotesk BundleWrapper ${isBestDeal ? "BestDeal" : ""}`}>
        {isBestDeal && (
          <span className="d-inline-flex align-items-center SpecialFontSettings BestDealText">
            {t("best_deal")}
          </span>
        )}

        <div className={`BundleOfferDetails ${isSelected ? "Selected" : ""}`}>
          {isSelected && (
            <span
              className={`checkmark-icon-wrapper ${
                isBestDeal ? "best-deal" : ""
              }`}
            >
              <CheckmarkCircleIcon />
            </span>
          )}
          {hasBonusOffer && <BonusTag />}
          <Stack
            direction="horizontal"
            className={`align-items-center justify-content-center BundleOffers ${
              isBestDeal || isStandalone ? "IsStandalone" : ""
            } ${hasBonusOffer ? "HasBonusOffer" : ""}`}
          >
            <div className="position-relative">
              <Image
                src={ImageHyperCoinStack}
                width={isBestDeal ? 63.2 : 45.4}
                height={isBestDeal ? 43.3 : 31.1}
                alt="Hyper coins"
              />
              <p
                className={`SpecialFontSettings HprAmount ${
                  isBestDeal || isStandalone ? "IsStandalone" : ""
                } ${isBestDeal ? "IsBestDeal" : ""}`}
              >
                {isFiatDisplay ? hprDisplayValue : formatHprBalance(amount, 3)}
              </p>
            </div>
            {hasBonusOffer && (
              <Stack
                direction="horizontal"
                gap={1}
                className="align-self-center d-flex align-items-center BonusOfferDetails"
              >
                <ExtraTag />
                {finalBonusHprOffer > 0 && (
                  <div className="position-relative">
                    <Image
                      src={ImageBonusHyperCoinStack}
                      alt="Hyper coins"
                      width={isBestDeal || isStandalone ? 38 : 27.4}
                      height={isBestDeal || isStandalone ? 23.4 : 16.9}
                    />
                    <p
                      className={`SpecialFontSettings BonusOfferHprAmount ${
                        isBestDeal || isStandalone ? "IsStandalone" : ""
                      }`}
                    >
                      {isFiatDisplay
                        ? `+${bonusHprDisplayValue}`
                        : `+${formatHprBalance(finalBonusHprOffer, 3)}`}
                    </p>
                  </div>
                )}
                {finalBonusGemOffer > 0 && (
                  <div className="position-relative">
                    <Image
                      src={ImageTwoGems}
                      alt="Gems"
                      width={isBestDeal || isStandalone ? 30 : 26.8}
                      height={isBestDeal || isStandalone ? 25 : 20.5}
                    />
                    <p
                      className={`SpecialFontSettings BonusOfferGemAmount ${
                        isBestDeal || isStandalone ? "IsStandalone" : ""
                      }`}
                    >
                      +{formatHprBalance(bonusGemOffer, 3)}
                    </p>
                  </div>
                )}
              </Stack>
            )}
          </Stack>

          <div
            className={`PriceContainer ${
              isBestDeal || isStandalone ? "IsStandalone" : ""
            } ${isSpecialOffer ? "WelcomeBonusBundle" : ""}`}
          >
            <Stack
              className="align-items-center justify-content-center BundlePrices"
              direction="horizontal"
              gap={1}
            >
              <p className="SpecialFontSettings DiscountedPrice">
                {formatCurrency(price, sourceCurrency, 2)}
              </p>
              {originalBundlePrice > 0 && (
                <p className="SpecialFontSettings NormalPrice">
                  {formatCurrency(originalBundlePrice, sourceCurrency, 2)}
                </p>
              )}
            </Stack>
          </div>
        </div>
      </div>
    </button>
  );
}

export default TopupBundle;
